import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Technician } from '../../../types/technician-types';
import { DateUtils } from '../../../utils';

interface TravelerFooterProps {
  completionDate: string;
  qcSignOffTechnicianId: string;
  /** Resolved name for signed-off technicians no longer in the active list. */
  qcSignOffTechnicianName?: string | null;
  technicians: Technician[];
  onCompletionDateChange: (date: string) => void;
  onQcSignOffChange: (technicianId: string) => void;
  onSignOff: () => void;
  disabled: boolean;
  /** Shows a saving indicator when the footer fields are being persisted. */
  saving?: boolean;
  /** Disables the entire footer (all inputs + button) until the order is ready for sign-off. */
  readyForSignOff?: boolean;
  /** Tooltip explaining why the footer is disabled. */
  notReadyReason?: string;
  /** True when the order-level QC sign-off has already been recorded. */
  alreadySignedOff?: boolean;
}

const TravelerFooter = ({
  completionDate,
  qcSignOffTechnicianId,
  qcSignOffTechnicianName,
  technicians,
  onCompletionDateChange,
  onQcSignOffChange,
  onSignOff,
  disabled,
  saving = false,
  readyForSignOff = false,
  notReadyReason,
  alreadySignedOff = false,
}: TravelerFooterProps) => {
  const allDisabled = disabled || (!readyForSignOff && !alreadySignedOff);
  const canSignOff =
    !allDisabled && readyForSignOff && !!qcSignOffTechnicianId && !!completionDate && !saving;

  // The date/technician live inside MUI form controls that are hidden in the PDF
  // export. These read-only strings are hidden on screen but revealed in the PDF
  // (see injected `.pdf-readonly-value` styles in work-order-traveler-page).
  const qcSignOffTechnician = technicians.find(t => String(t.id) === String(qcSignOffTechnicianId));
  const qcSignOffName = qcSignOffTechnician
    ? `${qcSignOffTechnician.firstName} ${qcSignOffTechnician.lastName}`
    : qcSignOffTechnicianName
      ? `${qcSignOffTechnicianName} (Inactive)`
      : '';

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: '1px solid #ccc',
        borderRadius: '4px',
        p: '16px 20px',
        mt: 3,
        display: 'flex',
        gap: 4,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontWeight: 500, fontSize: '13px', color: '#333' }}>
          Completion Date:
        </Typography>
        <Tooltip title={allDisabled && !disabled && notReadyReason ? notReadyReason : ''} arrow>
          <span>
            <TextField
              type="date"
              size="small"
              value={completionDate}
              onChange={e => onCompletionDateChange(e.target.value)}
              disabled={allDisabled}
              InputProps={{ sx: { fontSize: '13px' } }}
              sx={{ width: 160 }}
            />
          </span>
        </Tooltip>
        <Typography
          className="pdf-readonly-value"
          sx={{ display: 'none', fontSize: '13px', color: '#333', fontWeight: 500 }}
        >
          {completionDate ? DateUtils.toDisplayDate(completionDate) : ''}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontWeight: 500, fontSize: '13px', color: '#333' }}>
          Quality Control Sign Off:
        </Typography>
        <Tooltip title={allDisabled && !disabled && notReadyReason ? notReadyReason : ''} arrow>
          <span>
            <FormControl size="small" sx={{ width: 180 }}>
              <Select
                value={qcSignOffTechnicianId}
                onChange={e => onQcSignOffChange(e.target.value)}
                disabled={allDisabled}
                displayEmpty
                sx={{ fontSize: '13px' }}
              >
                <MenuItem value="">pick name</MenuItem>
                {alreadySignedOff &&
                  qcSignOffTechnicianId &&
                  !technicians.some(t => String(t.id) === String(qcSignOffTechnicianId)) && (
                    <MenuItem
                      value={String(qcSignOffTechnicianId)}
                      disabled
                      sx={{ fontSize: '13px', fontStyle: 'italic', opacity: 0.7 }}
                    >
                      {qcSignOffTechnicianName ?? 'Inactive Technician'} (Inactive)
                    </MenuItem>
                  )}
                {technicians.map(tech => (
                  <MenuItem key={tech.id} value={String(tech.id)}>
                    {tech.firstName} {tech.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </span>
        </Tooltip>
        <Typography
          className="pdf-readonly-value"
          sx={{ display: 'none', fontSize: '13px', color: '#333', fontWeight: 500 }}
        >
          {qcSignOffName}
        </Typography>
      </Box>
      {!alreadySignedOff && (
        <Tooltip
          title={
            !readyForSignOff && notReadyReason
              ? notReadyReason
              : !completionDate
                ? 'Enter a completion date first'
                : !qcSignOffTechnicianId
                  ? 'Select a technician first'
                  : ''
          }
          arrow
        >
          <span>
            <Button
              variant="contained"
              color="success"
              size="small"
              disabled={!canSignOff}
              onClick={onSignOff}
              sx={{ fontWeight: 600, textTransform: 'none', px: 3 }}
            >
              QC Sign Off
            </Button>
          </span>
        </Tooltip>
      )}
      {alreadySignedOff && (
        <Typography sx={{ fontSize: '13px', color: 'success.main', fontWeight: 600 }}>
          ✓ QC Signed Off
        </Typography>
      )}
      {saving && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CircularProgress size={14} />
          <Typography sx={{ fontSize: '12px', color: '#666' }}>Saving...</Typography>
        </Box>
      )}
    </Box>
  );
};

export default TravelerFooter;
